import { breadcrumbSchema, serviceSchema, faqSchema } from '../lib/seo'
import { GENERAL_FAQS } from './faqs'

export interface RouteSeo {
  title: string
  description: string
  jsonLd?: object[]
  noIndex?: boolean
}

/**
 * Metadata for every static route.
 *
 * Titles lead with the subject rather than the brand, because a search result
 * and an answer engine citation both read the first few words only. Each
 * description answers one question in one sentence, so it can be quoted whole.
 *
 * Dynamic routes carry their own metadata on the page itself, since it depends
 * on the record being rendered.
 */
export const ROUTE_SEO: Record<string, RouteSeo> = {
  '/': {
    title: 'Market intelligence and augmented intelligence for African organisations',
    description:
      'Cavalla Solution turns market intelligence, data led growth and augmented intelligence into evidence leaders can take into the room where the decision is made.',
    jsonLd: [faqSchema(GENERAL_FAQS)],
  },
  '/services': {
    title: 'Our services: intelligence, growth and augmented intelligence',
    description:
      'Three advisory lines and one platform. Cavalla Intelligence sizes the market, Cavalla Growth builds demand, Cavalla AI delivers governed adoption, and Cavalla Gov serves government.',
    jsonLd: [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }])],
  },
  '/augmented-intelligence': {
    title: 'Augmented intelligence: capture, apply, enable, decide',
    description:
      'Our method has four steps. We capture the evidence, apply machine analysis with human judgement, enable your team to run it, and leave the decision with a named person.',
    jsonLd: [
      breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Augmented intelligence', path: '/augmented-intelligence' },
      ]),
    ],
  },
  '/about': {
    title: 'About Cavalla Solution',
    description:
      'Cavalla Solution is a consulting firm in market intelligence, data led growth and augmented intelligence, founded by Jonathan Clarke and serving African and global organisations.',
    jsonLd: [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About us', path: '/about' }])],
  },
  '/insights': {
    title: 'Insights on African markets, data and augmented intelligence',
    description:
      'Analysis on African market sizing, data foundations and governed AI adoption, written for the leaders who have to act on it. Filter by service line and by sector.',
    jsonLd: [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Insights', path: '/insights' }])],
  },
  '/resources': {
    title: 'Free eBooks and guides on AI readiness and market entry',
    description:
      'Download our guides on AI readiness, data foundations and market entry in Africa. Each one is written from work we have delivered, and is free in exchange for an email.',
    jsonLd: [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Resources', path: '/resources' }])],
  },
  '/assessment': {
    title: 'Free AI readiness assessment, scored in five minutes',
    description:
      'Answer twelve questions across data, skills, use cases, governance and budget, and get a score out of one hundred with three recommended next steps, free.',
    jsonLd: [
      breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'AI readiness assessment', path: '/assessment' }]),
      serviceSchema({
        name: 'AI readiness assessment',
        description:
          'A free twelve question assessment that scores an organisation out of one hundred on data, skills, use cases, governance and budget, and returns three recommended next steps.',
        path: '/assessment',
        serviceType: 'Artificial intelligence readiness assessment',
        audience: 'Leaders and managers in NGOs, government, banking, health, agriculture and small and medium enterprises',
      }),
    ],
  },
  '/contact': {
    title: 'Contact us and book a discovery call',
    description:
      'Book a thirty minute discovery call, or send us the decision in front of you. We route your enquiry to the right service line and reply within two working days.',
    jsonLd: [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])],
  },
  '/legal/privacy': {
    title: 'Privacy notice',
    description:
      'How Cavalla Solution collects, uses and holds personal data, the lawful basis for each use, and how to exercise your rights. Personal data is held in the European Union.',
    noIndex: false,
  },
  '/legal/terms': {
    title: 'Terms of use',
    description:
      'The terms on which Cavalla Solution provides this website, including our position on proposals and fees, and the limits of the information published here.',
  },
  '/legal/cookies': {
    title: 'Cookie notice',
    description:
      'The cookies this site sets, what each one does, and how to change your choice. Analytics and marketing cookies are not set until you consent.',
  },
}

export function routeSeo(path: string): RouteSeo | undefined {
  return ROUTE_SEO[path === '/' ? '/' : path.replace(/\/$/, '')]
}
